import React, { useEffect, useState } from "react";
import { X, Smartphone, Download, Share, Plus } from "lucide-react";

// Detect platform
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent || "") && !window.MSStream;
const isAndroid = () => /Android/i.test(navigator.userAgent || "");
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const DISMISS_KEY = "adn_install_dismissed_at";
const DISMISS_DAYS = 14;

function wasDismissedRecently() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}
function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    // Android / Chrome / Edge
    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari: no beforeinstallprompt — show manual instructions after 8s on iOS
    if (isIOS()) {
      const t = setTimeout(() => setShowIOS(true), 8000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setShow(false);
    setDeferred(null);
  };

  const dismiss = () => { markDismissed(); setShow(false); setShowIOS(false); };

  if (!show && !showIOS) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[60] fade-up">
      <div className="bg-[var(--adn-navy)] text-white shadow-2xl border border-[var(--adn-gold)]/30 p-5 grain">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-sm bg-[var(--adn-gold)] flex items-center justify-center text-[var(--adn-navy)] font-display font-bold shrink-0">A</div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-semibold text-[15px]">Install ADN Marketplace</p>
            {show ? (
              <p className="text-white/70 text-xs mt-1 leading-relaxed">Add ADN to your home screen for fast access, offline-friendly browsing, and push-style alerts.</p>
            ) : (
              <p className="text-white/70 text-xs mt-1 leading-relaxed">
                Tap <Share size={12} className="inline mx-0.5 text-[var(--adn-gold)]" /> Share, then{" "}
                <span className="text-[var(--adn-gold)] font-semibold">Add to Home Screen</span>{" "}
                <Plus size={12} className="inline mx-0.5 text-[var(--adn-gold)]" />
              </p>
            )}
          </div>
          <button data-testid="install-dismiss" onClick={dismiss} className="text-white/60 hover:text-white p-1 -mr-1 -mt-1"><X size={16} /></button>
        </div>
        {show && (
          <div className="mt-4 flex gap-2">
            <button data-testid="install-now" onClick={install} className="btn-gold px-4 py-2 rounded text-sm inline-flex items-center gap-2 flex-1 justify-center">
              <Download size={14} /> Install
            </button>
            <button onClick={dismiss} className="btn-outline-light px-4 py-2 rounded text-sm">Not now</button>
          </div>
        )}
      </div>
    </div>
  );
}
